import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import LoadingSpinner from "../../Components/spinner";
import { toast } from "sonner";
import {
  addNewProvider,
  updateProvider,
  uploadProvidersImage,
} from "../../services/axios/apiManager";
import { handleErrors, maskContactNumber } from "../../services/dataManager";
import { baseURL } from "../../services/axios/eps";
import { useNavigate } from "react-router-dom";

const AddEditProviderModal = ({
  selectedProvider,
  setSelectedProvider,
  showModal,
  setShowModal,
  fetchProviders,
  pagination,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageURL, setImageURL] = useState(null);

  // // // Close Add Edit Tag Modal
  const handleCloseAddEditModal = () => {
    setSelectedProvider({});
    setImageURL(null);
    setShowModal(false);
  };

  const gameProviderSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .max(255, "Name cannot exceed 255 characters")
      .required("Name is required"),

    contact_email: Yup.string()
      .trim()
      .email("Invalid email format")
      .max(255, "Email cannot exceed 255 characters")
      .required("Contact email is required"),

    website: Yup.string()
      .trim()
      .url("Invalid URL format")
      .max(255, "Website URL cannot exceed 255 characters")
      .nullable(),
    phone: Yup.string()
      .trim()
      .matches(
        /^(?:\+(\d{1,3})\s?)?(\(\d{3}\)\s?\d{3}-\d{4})$/,
        "Invalid phone number format. { e.g., +1 (123) 456-7890 or (123) 456-7890 }"
      )
      .nullable(),
  });

  const handleUploadLogo = async (id, image) => {
    const formData = new FormData();
    formData.append("image", image);
    const res = await uploadProvidersImage(id, formData);
    if (!res?.data?.success) {
      handleErrors(res, navigate, "Upload Logo:");
    }
  };
  // // Add Edit Role Logic
  const handleAddEditTag = async (values) => {
    const { logo, ...data } = values;
    if (selectedProvider?.id) {
      const editResponse = await updateProvider({
        id: selectedProvider.id,
        ...data,
      });

      if (editResponse?.data?.success) {
        await handleUploadLogo(selectedProvider?.id, logo);
        await fetchProviders(pagination);
        toast.success("Game Provider has been updated!", { duration: 2000 });
        handleCloseAddEditModal();
      } else {
        toast.error("Game Provider has not been updated, try again!", {
          duration: 2000,
        });
      }
    } else {
      const addResponse = await addNewProvider(data);
      if (addResponse?.data?.success && addResponse?.data?.data) {
        const { id } = addResponse?.data?.data;
        await handleUploadLogo(id, logo);
        await fetchProviders(pagination);
        toast.success("Game Provider has been added", {
          duration: 2000,
        });
        handleCloseAddEditModal();
      } else {
        toast.error("Game Provider has not been added, try again!", {
          duration: 2000,
        });
      }
    }
  };
  const handleImageChange = async (e, setFieldValue) => {
    e.preventDefault();
    if (e?.target?.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFieldValue("logo", file);
      const previewUrl = URL.createObjectURL(file);
      setImageURL(previewUrl);
    } else {
      setImageURL(null);
      setFieldValue("logo", selectedProvider.logo || null);
    }
  };
  useEffect(() => {
    if (!showModal) {
      setImageURL(null);
    }
  }, [showModal]);
  return (
    <Modal
      show={showModal}
      onHide={handleCloseAddEditModal}
      centered
      className="text-light"
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-dark text-light border-0">
        <Modal.Title>
          {selectedProvider?.id ? "Edit Game Provider" : "Add Game Provider"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Formik
          initialValues={{
            name: selectedProvider?.name || "",
            contact_email: selectedProvider?.contact_email || "",
            website: selectedProvider?.website || "",
            phone: selectedProvider?.phone || "",
            logo: selectedProvider?.logo || "",
          }}
          enableReinitialize={true}
          validationSchema={gameProviderSchema}
          onSubmit={async (values) => {
            try {
              setLoading(true);
              await handleAddEditTag(values);
            } catch (error) {
              console.log("Error upon Add Update Tag", error);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Provider Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter provider name"
                  required
                  value={values.name}
                  onChange={handleChange("name")}
                  className="bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.name && touched?.name ? (
                  <span className="text-danger">{errors?.name}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter email address"
                  required
                  value={values.contact_email}
                  onChange={handleChange("contact_email")}
                  className="bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.contact_email && touched?.contact_email ? (
                  <span className="text-danger">{errors?.contact_email}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter website"
                  required
                  value={values.website}
                  onChange={handleChange("website")}
                  className="bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.website && touched?.website ? (
                  <span className="text-danger">{errors?.website}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter phone number"
                  required
                  value={values.phone}
                  onChange={(event) => {
                    setFieldValue("phone", maskContactNumber(event));
                  }}
                  className="bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.phone && touched?.phone ? (
                  <span className="text-danger">{errors?.phone}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Provider Logo</Form.Label>
                <Form.Control
                  type="file"
                  className="bg-dark text-light border-light"
                  data-testid="Provider-Logo"
                  onChange={(e) => {
                    handleImageChange(e, setFieldValue);
                  }}
                />
                {imageURL || selectedProvider?.logo ? (
                  <img
                    crossorigin="anonymous"
                    src={
                      imageURL
                        ? imageURL
                        : `${baseURL}/uploads/file/${selectedProvider?.logo}`
                    }
                    alt="Provider"
                    className="img-fluid rounded center"
                    style={{
                      maxHeight: "150px",
                      objectFit: "contain",
                      marginTop: "10px",
                    }}
                  />
                ) : null}
              </Form.Group>
              <div className="constainer-fluid">
                <div className="d-flex justify-content-end">
                  {loading && (
                    <div className="mt-4 mx-2">
                      <LoadingSpinner size={"lg"} />
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleCloseAddEditModal();
                    }}
                    className="mt-3 mx-2"
                  >
                    Close
                  </Button>
                  <Button
                    variant="success"
                    className="mt-3"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    {selectedProvider?.id ? "Update" : "Add New Provider"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
export default AddEditProviderModal;
